/**
 * TUVI - Componente Genérico para Capítulos Técnicos
 * Maneja todos los capítulos de construcción con fechas, progreso y notas
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import client from '../../../services/graphqlClient';
import './CapituloTecnico.css';

const CapituloTecnico = ({ capitulo, onUpdate, onVerActividades, proyectoId }) => {
  const [editMode, setEditMode] = useState(false);
  // Caja menor removida - ahora es módulo independiente
  const [formData, setFormData] = useState({
    fecha_inicio_cro: capitulo?.fecha_inicio_cro || '',
    fecha_fin_cro: capitulo?.fecha_fin_cro || '',
    fecha_inicio: capitulo?.fecha_inicio || '',
    fecha_fin: capitulo?.fecha_fin || '',
    progreso: capitulo?.progreso || 0,
    notas: capitulo?.notas || ''
  });

  // Contratista autocomplete state
  const [contratistas, setContratistas] = useState([]);
  const [contratistaSearch, setContratistaSearch] = useState('');
  const [showContratistaDropdown, setShowContratistaDropdown] = useState(false);
  const [contratistaSeleccionado, setContratistaSeleccionado] = useState(
    capitulo?.contratista_id ? { id: capitulo.contratista_id, nombre: capitulo.contratista_nombre || '', nit: capitulo.contratista_nit || '' } : null
  );
  const [savingContratista, setSavingContratista] = useState(false);
  const contratistaRef = useRef(null);

  // Fetch contratistas list
  useEffect(() => {
    const fetchContratistas = async () => {
      try {
        const data = await client.query(`{ proveedores(clasificacion: "contratista") { proveedores { id nombre nit } } }`);
        setContratistas(data.proveedores.proveedores || []);
      } catch (err) {
        console.error('Error fetching contratistas:', err);
      }
    };
    fetchContratistas();
  }, []);

  // Update local state when capitulo prop changes
  useEffect(() => {
    if (capitulo?.contratista_id) {
      setContratistaSeleccionado({
        id: capitulo.contratista_id,
        nombre: capitulo.contratista_nombre || '',
        nit: capitulo.contratista_nit || ''
      });
    } else {
      setContratistaSeleccionado(null);
    }
  }, [capitulo?.contratista_id, capitulo?.contratista_nombre, capitulo?.contratista_nit]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contratistaRef.current && !contratistaRef.current.contains(e.target)) {
        setShowContratistaDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter contratistas based on search
  const contratistasFiltrados = contratistas.filter(c => {
    if (!contratistaSearch) return true;
    const search = contratistaSearch.toLowerCase();
    return (c.nombre && c.nombre.toLowerCase().includes(search)) ||
           (c.nit && c.nit.toLowerCase().includes(search));
  });

  // Save contratista assignment
  const guardarContratista = useCallback(async (contratistaId) => {
    if (!capitulo?.id) return;
    setSavingContratista(true);
    try {
      const data = await client.mutate(`
        mutation($capitulo_id: Int!, $input: ActualizarCapituloInput!) {
          actualizarCapitulo(capitulo_id: $capitulo_id, input: $input) { success message }
        }
      `, { capitulo_id: capitulo.id, input: { contratista_id: contratistaId } });
      if (!data.actualizarCapitulo.success) {
        console.error('Error saving contratista:', data.actualizarCapitulo.message);
      }
    } catch (err) {
      console.error('Error saving contratista:', err);
    } finally {
      setSavingContratista(false);
    }
  }, [capitulo?.id]);

  // Handle contratista selection
  const handleSelectContratista = (contratista) => {
    setContratistaSeleccionado(contratista);
    setContratistaSearch('');
    setShowContratistaDropdown(false);
    guardarContratista(contratista.id);
  };

  // Handle unassign contratista
  const handleRemoveContratista = () => {
    setContratistaSeleccionado(null);
    setContratistaSearch('');
    guardarContratista(null);
  };

  // Información de capítulos
  const capitulosInfo = {
    infraestructura_redes: {
      titulo: 'Infraestructura y Redes',
      icono: '🔌',
      descripcion: 'Gestión de instalación de redes hidráulicas, sanitarias y eléctricas básicas',
      items: [
        'Instalación de tuberías de agua',
        'Sistema de desagüe y alcantarillado',
        'Puntos eléctricos básicos',
        'Cableado estructurado',
        'Pruebas de presión'
      ]
    },
    certificado_gas: {
      titulo: 'Certificado de Gas',
      icono: '🔥',
      descripcion: 'Instalación y certificación del sistema de gas natural',
      items: [
        'Instalación de tubería de gas',
        'Puntos de salida',
        'Válvulas de seguridad',
        'Pruebas de hermeticidad',
        'Certificación oficial'
      ]
    },
    instalacion_electrica: {
      titulo: 'Instalación Eléctrica',
      icono: '💡',
      descripcion: 'Sistema eléctrico completo, tableros y puntos de conexión',
      items: [
        'Tablero eléctrico principal',
        'Circuitos independientes',
        'Puntos de iluminación',
        'Tomas corriente',
        'Pruebas de continuidad'
      ]
    },
    mortero: {
      titulo: 'Mortero',
      icono: '🏗️',
      descripcion: 'Aplicación de mortero en pisos y bases',
      items: [
        'Nivelación de pisos',
        'Aplicación de mortero',
        'Tiempos de secado',
        'Control de calidad',
        'Verificación de niveles'
      ]
    },
    revoque: {
      titulo: 'Revoque',
      icono: '🧱',
      descripcion: 'Revoque de muros y superficies',
      items: [
        'Preparación de superficies',
        'Aplicación de revoque',
        'Plomada y nivel',
        'Secado controlado',
        'Inspección final'
      ]
    },
    argamasa_estuco_fondeo: {
      titulo: 'Argamasa, Estuco y Fondeo',
      icono: '🎨',
      descripcion: 'Acabados finos previos a pintura',
      items: [
        'Aplicación de argamasa',
        'Estuco fino',
        'Fondeo de superficies',
        'Lijado y pulido',
        'Preparación para pintura'
      ]
    },
    cajones: {
      titulo: 'Cajones',
      icono: '📦',
      descripcion: 'Instalación de cajas eléctricas y registros',
      items: [
        'Cajas eléctricas',
        'Cajas telefónicas',
        'Registros de inspección',
        'Tapas y acabados',
        'Verificación de funcionamiento'
      ]
    },
    enchapes: {
      titulo: 'Enchapes',
      icono: '🪟',
      descripcion: 'Instalación de enchapes y cerámicas',
      items: [
        'Enchape de baños',
        'Enchape de cocina',
        'Guardaescobas',
        'Fragüe y limpieza',
        'Control de calidad'
      ]
    },
    cocina_pisende: {
      titulo: 'Cocina Pisende',
      icono: '🍳',
      descripcion: 'Instalación de muebles de cocina',
      items: [
        'Muebles inferiores',
        'Muebles superiores',
        'Nivelación y fijación',
        'Herrajes y bisagras',
        'Pruebas de funcionamiento'
      ]
    },
    carpinteria: {
      titulo: 'Carpintería',
      icono: '🚪',
      descripcion: 'Instalación de puertas, ventanas y marcos',
      items: [
        'Puertas principales',
        'Puertas interiores',
        'Marcos y chambrana',
        'Cerraduras y chapas',
        'Ajustes finales'
      ]
    },
    mesones: {
      titulo: 'Mesones',
      icono: '🪵',
      descripcion: 'Instalación de mesones de cocina y baños',
      items: [
        'Medición y corte',
        'Instalación de mesones',
        'Salpicaderos',
        'Sellado de juntas',
        'Lavaplatos y grifería'
      ]
    },
    salpicadero_cocina: {
      titulo: 'Salpicadero Cocina',
      icono: '✨',
      descripcion: 'Instalación de salpicadero y protección de mesones',
      items: [
        'Medición y corte',
        'Aplicación de adhesivo',
        'Instalación',
        'Fragüe y sellado',
        'Limpieza final'
      ]
    },
    instalacion_paneles_led: {
      titulo: 'Instalación Paneles LED',
      icono: '💡',
      descripcion: 'Instalación de iluminación LED',
      items: [
        'Lámparas LED',
        'Paneles empotrados',
        'Conexiones eléctricas',
        'Pruebas de funcionamiento',
        'Reguladores de intensidad'
      ]
    },
    acabado_pintura: {
      titulo: 'Acabado y Pintura',
      icono: '🖌️',
      descripcion: 'Pintura final de todas las áreas',
      items: [
        'Primera mano de pintura',
        'Segunda mano',
        'Retoques finales',
        'Limpieza de salpicaduras',
        'Inspección de acabados'
      ]
    },
    cabinas_espejos: {
      titulo: 'Cabinas y Espejos',
      icono: '🪞',
      descripcion: 'Instalación de espejos y cabinas de baño',
      items: [
        'Espejos de baños',
        'Cabinas de ducha',
        'Accesorios de baño',
        'Nivelación y fijación',
        'Pruebas de funcionamiento'
      ]
    },
    aparatos_apartamento: {
      titulo: 'Aparatos del Apartamento',
      icono: '🔧',
      descripcion: 'Instalación de aparatos y equipos',
      items: [
        'Lavadora',
        'Calentador',
        'Extractores',
        'Aires acondicionados',
        'Pruebas de funcionamiento'
      ]
    },
    aseo_limpieza: {
      titulo: 'Aseo y Limpieza',
      icono: '🧹',
      descripcion: 'Limpieza profunda final del apartamento',
      items: [
        'Limpieza de pisos',
        'Limpieza de vidrios',
        'Retiro de escombros',
        'Desinfección general',
        'Verificación final'
      ]
    },
    entrega_final: {
      titulo: 'Entrega Final',
      icono: '🎉',
      descripcion: 'Proceso de entrega al cliente',
      items: [
        'Inspección final',
        'Lista de verificación',
        'Entrega de llaves',
        'Manuales y garantías',
        'Acta de entrega firmada'
      ]
    }
  };

  const info = capitulosInfo[capitulo?.capitulo_nombre] || {
    titulo: capitulo?.capitulo_nombre || 'Capítulo',
    icono: '📋',
    descripcion: 'Información del capítulo',
    items: []
  };

  // Formatear fecha para input
  const formatDateForInput = (fecha) => {
    if (!fecha) return '';
    const date = new Date(fecha);
    return date.toISOString().split('T')[0];
  };

  // Formatear fecha para mostrar
  const formatDate = (fecha) => {
    if (!fecha) return 'No definida';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Calcular días de diferencia
  const calcularDiferencia = (fechaReal, fechaCro) => {
    if (!fechaReal || !fechaCro) return null;
    const real = new Date(fechaReal);
    const cro = new Date(fechaCro);
    const diff = Math.floor((real - cro) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Obtener estado del capítulo
  const getEstado = () => {
    if (capitulo?.fecha_fin) return 'completado';
    if (capitulo?.fecha_fin_cro && new Date(capitulo.fecha_fin_cro) < new Date()) return 'retrasado';
    if (capitulo?.fecha_inicio) return 'en_progreso';
    return 'pendiente';
  };

  const estado = getEstado();
  const diasRetrasoInicio = calcularDiferencia(capitulo?.fecha_inicio, capitulo?.fecha_inicio_cro);
  const diasRetrasoFin = calcularDiferencia(capitulo?.fecha_fin, capitulo?.fecha_fin_cro);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onUpdate) {
      onUpdate(capitulo.id, formData);
    }
    setEditMode(false);
  };

  const handleCancel = () => {
    setFormData({
      fecha_inicio_cro: capitulo?.fecha_inicio_cro || '',
      fecha_fin_cro: capitulo?.fecha_fin_cro || '',
      fecha_inicio: capitulo?.fecha_inicio || '',
      fecha_fin: capitulo?.fecha_fin || '',
      progreso: capitulo?.progreso || 0,
      notas: capitulo?.notas || ''
    });
    setEditMode(false);
  };

  return (
    <div className="capitulo-tecnico">
      {/* Header del Capítulo */}
      <div className="capitulo-tecnico-header">
        <div className="header-left">
          <span className="header-icon">{info.icono}</span>
          <div className="header-text">
            <h2>{info.titulo}</h2>
            <p>{info.descripcion}</p>
          </div>
        </div>
        <div className="header-right">
          <span className={`estado-badge ${estado}`}>
            {estado === 'completado' && '✓ Completado'}
            {estado === 'en_progreso' && '▶ En Progreso'}
            {estado === 'retrasado' && '⚠ Retrasado'}
            {estado === 'pendiente' && '⏸ Pendiente'}
          </span>
          {onVerActividades && (
            <button className="btn-actividades" onClick={onVerActividades}>
              📋 Actividades
            </button>
          )}
        </div>
      </div>

      {/* Progreso Visual - basado en porcentaje de ejecución de gastos */}
      <div className="progreso-section">
        <div className="progreso-header">
          <span className="progreso-label">Progreso del Capítulo (Ejecución)</span>
          <span className={`progreso-value ${(capitulo?.porcentaje_ejecucion || 0) > 100 ? 'excede' : ''}`}>
            {capitulo?.porcentaje_ejecucion || 0}%
          </span>
        </div>
        <div className="progreso-bar">
          <div
            className={`progreso-fill ${(capitulo?.porcentaje_ejecucion || 0) > 100 ? 'excede' : ''}`}
            style={{ width: `${Math.min(capitulo?.porcentaje_ejecucion || 0, 100)}%` }}
          ></div>
        </div>
      </div>

      {/* Control de Presupuesto */}
      {(() => {
        // Presupuesto = Valor / 1.15 (se le quita el 15% de AIU), igual que Control Presupuestal.
        const valorCap = capitulo?.subtotal || capitulo?.valor_actividades || 0;
        const presupuesto = valorCap / 1.15;
        const ejecutadoFacturas = capitulo?.valor_ejecutado_total || 0;
        // Salidas de bodega cargadas a este capítulo
        const salidasBodega = capitulo?.salidas_bodega || 0;
        const totalGastado = ejecutadoFacturas + salidasBodega;
        const saldo = presupuesto - totalGastado;
        const porcentajeTotal = presupuesto > 0 ? ((totalGastado / presupuesto) * 100).toFixed(1) : 0;
        const excedePresupuesto = totalGastado > presupuesto;
        const fmtCOP = (v) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);

        return (
          <div className={`presupuesto-section ${excedePresupuesto ? 'excede' : ''}`}>
            <div className="presupuesto-header">
              <span className="presupuesto-icono">$</span>
              <h3>Control de Presupuesto <span style={{ fontWeight: 400, fontSize: '0.8rem', color: '#64748b' }}>(Presupuesto = Valor ÷ 1.15)</span></h3>
              {excedePresupuesto && (
                <span className="alerta-exceso-badge">Excede Presupuesto</span>
              )}
            </div>
            <div className="presupuesto-grid presupuesto-grid-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
              <div className="presupuesto-item">
                <span className="presupuesto-label">Presupuesto</span>
                <span className="presupuesto-valor">{fmtCOP(presupuesto)}</span>
              </div>
              <div className="presupuesto-item">
                <span className="presupuesto-label">Ejecutado (Facturas)</span>
                <span className="presupuesto-valor">{fmtCOP(ejecutadoFacturas)}</span>
              </div>
              <div className="presupuesto-item">
                <span className="presupuesto-label">Salidas de Bodega</span>
                <span className="presupuesto-valor">{fmtCOP(salidasBodega)}</span>
              </div>
              <div className="presupuesto-item">
                <span className="presupuesto-label">% Total</span>
                <span className={`presupuesto-valor porcentaje ${excedePresupuesto ? 'excede' : ''}`}>{porcentajeTotal}%</span>
              </div>
              <div className="presupuesto-item">
                <span className="presupuesto-label">Saldo</span>
                <span className={`presupuesto-valor ${saldo < 0 ? 'negativo' : ''}`}>{fmtCOP(saldo)}</span>
              </div>
            </div>
            <div className="presupuesto-barra-container">
              <div className="presupuesto-barra">
                <div
                  className={`presupuesto-barra-fill ${excedePresupuesto ? 'excede' : ''}`}
                  style={{ width: `${Math.min(parseFloat(porcentajeTotal), 100)}%` }}
                ></div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Contratista Asignado */}
      <div className="contratista-section">
        <div className="contratista-header">
          <span className="contratista-icono-header">&#128119;</span>
          <h3>Contratista</h3>
        </div>
        <div className="contratista-body">
          {contratistaSeleccionado ? (
            <div className="contratista-asignado">
              <div className="contratista-info">
                <span className="contratista-nombre">{contratistaSeleccionado.nombre}</span>
                {contratistaSeleccionado.nit && (
                  <span className="contratista-nit">NIT: {contratistaSeleccionado.nit}</span>
                )}
              </div>
              <button
                className="btn-remove-contratista"
                onClick={handleRemoveContratista}
                disabled={savingContratista}
                title="Desasignar contratista"
              >
                &times;
              </button>
            </div>
          ) : (
            <div className="contratista-autocomplete" ref={contratistaRef}>
              <input
                type="text"
                className="contratista-search-input"
                placeholder="Buscar contratista por nombre o NIT..."
                value={contratistaSearch}
                onChange={(e) => {
                  setContratistaSearch(e.target.value);
                  setShowContratistaDropdown(true);
                }}
                onFocus={() => setShowContratistaDropdown(true)}
                disabled={savingContratista}
              />
              {showContratistaDropdown && contratistasFiltrados.length > 0 && (
                <ul className="contratista-dropdown">
                  {contratistasFiltrados.map((c) => (
                    <li
                      key={c.id}
                      className="contratista-dropdown-item"
                      onClick={() => handleSelectContratista(c)}
                    >
                      <span className="dropdown-nombre">{c.nombre}</span>
                      {c.nit && <span className="dropdown-nit">NIT: {c.nit}</span>}
                    </li>
                  ))}
                </ul>
              )}
              {showContratistaDropdown && contratistasFiltrados.length === 0 && contratistaSearch && (
                <div className="contratista-dropdown contratista-no-results">
                  No se encontraron contratistas
                </div>
              )}
            </div>
          )}
          {savingContratista && <span className="contratista-saving">Guardando...</span>}
        </div>
      </div>

      {/* Contenido Principal - Solo mostrar si hay contenido o estamos en modo edición */}
      {(editMode || info.items.length > 0 || capitulo?.notas) && (
        <div className="capitulo-content">
          {editMode ? (
            /* Modo Edición */
            <form onSubmit={handleSubmit} className="edit-form">
              <div className="form-section">
                <h3>📅 Fechas Programadas</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Fecha Inicio Programado:</label>
                    <input
                      type="date"
                      name="fecha_inicio_cro"
                      value={formatDateForInput(formData.fecha_inicio_cro)}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Fecha Fin Programado:</label>
                    <input
                      type="date"
                      name="fecha_fin_cro"
                      value={formatDateForInput(formData.fecha_fin_cro)}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>✅ Fechas Reales</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Fecha Inicio Real:</label>
                    <input
                      type="date"
                      name="fecha_inicio"
                      value={formatDateForInput(formData.fecha_inicio)}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Fecha Fin Real:</label>
                    <input
                      type="date"
                      name="fecha_fin"
                      value={formatDateForInput(formData.fecha_fin)}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>📊 Progreso</h3>
                <div className="form-group">
                  <label>Progreso (%):</label>
                  <input
                    type="range"
                    name="progreso"
                    min="0"
                    max="100"
                    value={formData.progreso}
                    onChange={handleInputChange}
                    className="progress-slider"
                  />
                  <span className="progress-display">{formData.progreso}%</span>
                </div>
              </div>

              <div className="form-section">
                <h3>📝 Notas y Observaciones</h3>
                <div className="form-group">
                  <textarea
                    name="notas"
                    value={formData.notas}
                    onChange={handleInputChange}
                    placeholder="Escribe notas, observaciones o detalles importantes..."
                    rows="5"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-guardar">
                  💾 Guardar Cambios
                </button>
                <button type="button" className="btn-cancelar" onClick={handleCancel}>
                  ✖ Cancelar
                </button>
              </div>
            </form>
          ) : (
            /* Modo Vista */
            <>
              {/* Lista de Actividades */}
              {info.items.length > 0 && (
                <div className="info-section">
                  <h3>✓ Actividades del Capítulo</h3>
                  <ul className="actividades-list">
                    {info.items.map((item, index) => (
                      <li key={index} className="actividad-item">
                        <span className="actividad-icon">□</span>
                        <span className="actividad-text">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Notas */}
              {capitulo?.notas && (
                <div className="info-section">
                  <h3>📝 Notas y Observaciones</h3>
                  <div className="notas-content">
                    {capitulo.notas}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CapituloTecnico;
